import type {
  Collection,
  Context,
  GetResult,
  InferResultType,
  InitialQueryBuilder,
  LiveQueryCollectionUtils,
  QueryBuilder,
} from "@tanstack/db";
import { useLiveQuery } from "@tanstack/react-db";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

/**
 * Type guard to check if utils object has setWindow method (LiveQueryCollectionUtils)
 */
function isLiveQueryCollectionUtils(
  utils: unknown,
): utils is LiveQueryCollectionUtils {
  return typeof (utils as LiveQueryCollectionUtils).setWindow === "function";
}

export interface UseLiveInfiniteCursorQueryConfig<
  TContext extends Context,
  TCursor,
> {
  pageSize?: number;
  /**
   * Function to extract a cursor from the last item of a page.
   * Return null/undefined when there are no more pages.
   */
  getCursor: (
    lastItem: InferResultType<TContext>[number] | undefined,
  ) => TCursor | null | undefined;
  /**
   * Function to fetch a page of data given a cursor.
   * Should return the items and the next cursor (or null if no more pages).
   */
  fetchPage: (
    cursor: TCursor | null,
  ) => Promise<{
    items: InferResultType<TContext>;
    nextCursor: TCursor | null;
  }>;
}

export interface UseLiveInfiniteCursorQueryReturn<TContext extends Context> {
  data: InferResultType<TContext>;
  pages: Array<InferResultType<TContext>>;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isReady: boolean;
  error: Error | null;
  collection: Collection<GetResult<TContext>, string | number, object>;
}

/**
 * Create an infinite query using cursor-based pagination with live updates.
 *
 * This hook combines TanStack DB's live query capabilities with cursor-based
 * pagination. The initial page is loaded via useLiveQuery for live updates,
 * and subsequent pages are fetched using the provided fetchPage function.
 *
 * @param queryFn - Query function that defines the initial data fetch. Must include `.orderBy()`.
 * @param config - Configuration including pageSize, getCursor, and fetchPage functions
 * @param deps - Array of dependencies that trigger query re-execution when changed
 * @returns Object with pages, data, and pagination controls
 */
export function useLiveInfiniteCursorQuery<TContext extends Context, TCursor>(
  queryFn: (q: InitialQueryBuilder) => QueryBuilder<TContext>,
  config: UseLiveInfiniteCursorQueryConfig<TContext, TCursor>,
  deps: Array<unknown> = [],
): UseLiveInfiniteCursorQueryReturn<TContext> {
  const { pageSize = 20, getCursor, fetchPage } = config;

  // State for additional pages loaded via cursor
  const [additionalPages, setAdditionalPages] = useState<
    Array<InferResultType<TContext>>
  >([]);
  const [nextCursor, setNextCursor] = useState<TCursor | null>(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isFetchingNextPage, setIsFetchingNextPage] = useState(false);
  const [isInitialCursorSet, setIsInitialCursorSet] = useState(false);
  const [fetchError, setFetchError] = useState<Error | null>(null);

  // Track deps for query functions
  const depsKey = JSON.stringify(deps);
  const prevDepsKeyRef = useRef(depsKey);

  // Reset pagination when deps change
  useEffect(() => {
    if (prevDepsKeyRef.current !== depsKey) {
      prevDepsKeyRef.current = depsKey;
      setAdditionalPages([]);
      setNextCursor(null);
      setHasNextPage(true);
      setIsInitialCursorSet(false);
      setFetchError(null);
    }
  }, [depsKey]);

  // Create live query for the first page
  // Always call useLiveQuery unconditionally to satisfy Rules of Hooks
  const queryResult = useLiveQuery((q) => {
    return queryFn(q).limit(pageSize + 1);
  }, deps);

  // Set up initial window for the live query
  useEffect(() => {
    const utils = queryResult.collection.utils;

    if (!isLiveQueryCollectionUtils(utils)) {
      return;
    }

    if (!queryResult.isReady) {
      return;
    }

    // Set window for initial page (pageSize + 1 for peek ahead)
    const result = utils.setWindow({ offset: 0, limit: pageSize + 1 });

    if (result !== true) {
      void result;
    }
  }, [queryResult.collection, queryResult.isReady, pageSize]);

  // Extract initial cursor from first page data when ready
  useEffect(() => {
    if (isInitialCursorSet) {
      return;
    }

    const dataArray = Array.isArray(queryResult.data) ? queryResult.data : [];
    if (dataArray.length === 0) {
      return;
    }

    // Check if there are more pages (peek ahead)
    const hasMore = dataArray.length > pageSize;
    setHasNextPage(hasMore);

    if (hasMore) {
      // Get cursor from the last displayed item (not the peek ahead item)
      const lastDisplayedItem = dataArray[pageSize - 1] as
        | InferResultType<TContext>[number]
        | undefined;
      const cursor = getCursor(lastDisplayedItem);
      setNextCursor(cursor ?? null);
    }

    setIsInitialCursorSet(true);
  }, [queryResult.data, pageSize, getCursor, isInitialCursorSet]);

  // Build pages and flat data, deduplicating by checking for id property
  const { pages, flatData } = useMemo(() => {
    const firstPageData = Array.isArray(queryResult.data)
      ? (queryResult.data.slice(0, pageSize) as InferResultType<TContext>)
      : ([] as unknown as InferResultType<TContext>);

    const allPages: Array<InferResultType<TContext>> = [
      firstPageData,
      ...additionalPages,
    ];

    // Flatten and deduplicate - items might appear in both live query and fetched pages
    const seen = new Set<string>();
    const deduped: Array<InferResultType<TContext>[number]> = [];

    for (const page of allPages) {
      for (const item of page) {
        // Try to extract an id from the item for deduplication
        const itemAny = item as Record<string, unknown>;
        const id =
          typeof itemAny.id === "string"
            ? itemAny.id
            : typeof (itemAny.conversation as Record<string, unknown>)?.id ===
                "string"
              ? ((itemAny.conversation as Record<string, unknown>).id as string)
              : null;

        if (id && seen.has(id)) {
          continue; // Skip duplicate
        }
        if (id) {
          seen.add(id);
        }
        deduped.push(item);
      }
    }

    return {
      pages: allPages,
      flatData: deduped as unknown as InferResultType<TContext>,
    };
  }, [queryResult.data, pageSize, additionalPages]);

  // Fetch next page using cursor
  const fetchNextPageFn = useCallback(async () => {
    // Don't fetch if there's an error, already fetching, no more pages, or no cursor
    if (
      fetchError ||
      !hasNextPage ||
      isFetchingNextPage ||
      nextCursor === null
    ) {
      return;
    }

    setIsFetchingNextPage(true);
    setFetchError(null);
    try {
      const result = await fetchPage(nextCursor);

      setAdditionalPages((prev) => [...prev, result.items]);
      setNextCursor(result.nextCursor);
      setHasNextPage(result.nextCursor !== null);
    } catch (error) {
      setFetchError(error instanceof Error ? error : new Error(String(error)));
      // Stop pagination on error - don't allow retries until explicitly reset
      setHasNextPage(false);
    } finally {
      setIsFetchingNextPage(false);
    }
  }, [hasNextPage, isFetchingNextPage, nextCursor, fetchError, fetchPage]);

  return {
    data: flatData,
    pages,
    fetchNextPage: fetchNextPageFn,
    hasNextPage,
    isFetchingNextPage,
    isLoading: queryResult.isLoading,
    isReady: queryResult.isReady,
    error: fetchError,
    collection: queryResult.collection,
  };
}
